import React from 'react'
import cn from 'classnames'

import DefaultLink from 'components/Link/Link'

import ArrowLeftIcon from './ArrowLeftIcon'
import ArrowRightIcon from './ArrowRightIcon'
import FillerIcon from './FillerIcon'

import styles from './Pagination.module.css'

const PageFiller = 0
const MIN_TOTAL_PAGES_TO_RENDER_PAGINATION = 1
const MIN_PAGES_RENDERED_TO_SHOW_DOUBLE_FILLERS = 6
const MAX_TOTAL_PAGES_TO_RENDER_ALL_PAGES_AT_ONCE = 7

type PaginationProps = {
  currentPage: number
  totalPages: number
  pathname?: string
  navigateCallback?: (page: number) => void
}

const FallbackLink: React.FC<unknown> = (props) => <span {...props} />

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pathname,
  navigateCallback,
}) => {
  if (totalPages <= MIN_TOTAL_PAGES_TO_RENDER_PAGINATION) {
    return null
  }
  let pages: number[] = []
  if (totalPages <= MAX_TOTAL_PAGES_TO_RENDER_ALL_PAGES_AT_ONCE) {
    pages = Array.from(Array(totalPages).keys())
    pages.shift()
    pages.push(totalPages)
  } else {
    pages = Array.from(
      new Set([
        1,
        Math.max(currentPage - 2, 1),
        Math.max(currentPage - 1, 1),
        currentPage,
        Math.min(currentPage + 1, totalPages),
        Math.min(currentPage + 2, totalPages),
        totalPages,
      ]),
    )

    if (pages.length <= MIN_PAGES_RENDERED_TO_SHOW_DOUBLE_FILLERS) {
      if (currentPage <= 3) {
        pages.splice(
          pages.length - (currentPage === 1 ? 1 : 2),
          currentPage === 1 ? 0 : 1,
          PageFiller,
        )
      }
      if (currentPage >= totalPages - 3) {
        pages.splice(1, currentPage === totalPages ? 0 : 1, PageFiller)
      }
    } else {
      pages.splice(1, 1, PageFiller)
      pages.splice(pages.length - 2, 1, PageFiller)
    }
  }

  const Link = navigateCallback ? FallbackLink : DefaultLink

  const linkProps = (pageNumber: number) => ({
    ...(navigateCallback
      ? {
          onClick: () => navigateCallback(pageNumber),
        }
      : {
          href: {
            pathname,
            query: {
              page: pageNumber,
            },
          },
        }),
  })

  return (
    <div className={styles.container}>
      <nav className={styles.navigation} data-cy="pagination-controls">
        <Link {...linkProps(Math.max(currentPage - 1, 1))}>
          <a className={styles.chevron} data-cy="pagination-controls-chevron">
            <ArrowLeftIcon />
          </a>
        </Link>
        <div className={styles.pages}>
          {pages.map((pageNumber, index) => {
            if (pageNumber === PageFiller) {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <FillerIcon key={`${pageNumber}-${index}`} />
              )
            }
            return (
              <Link key={pageNumber} {...linkProps(pageNumber)}>
                <a
                  className={cn(styles.link, {
                    [styles.activeLink]: currentPage === pageNumber,
                  })}
                  data-cy="pagination-controls-page"
                >
                  {pageNumber}
                </a>
              </Link>
            )
          })}
        </div>
        <Link {...linkProps(Math.min(currentPage + 1, totalPages))}>
          <a className={styles.chevron} data-cy="pagination-controls-chevron">
            <ArrowRightIcon />
          </a>
        </Link>
      </nav>
    </div>
  )
}

export default Pagination
